import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import Select from 'react-select'; // Import react-select
import DatePicker from 'react-datepicker'; // Import react-datepicker
import "react-datepicker/dist/react-datepicker.css"; // Import datepicker styles
import { toast } from 'react-toastify';
import { FaSearch, FaFilter, FaDownload, FaPlus, FaClock, FaUser, FaTimes, FaTag, FaBox, FaCheckCircle, FaCalendarAlt } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ActivityLogsNew = () => {
  const { user } = useContext(AuthContext);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterItem, setFilterItem] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [references, setReferences] = useState([]);
  const [guests, setGuests] = useState([]);
  const [stations, setStations] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    items: [],
    statuses: []
  });
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [pieChartData, setPieChartData] = useState({
    labels: [],
    datasets: []
  });
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
    draft: 0,
    service: 0,
    quotationIssued: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    date: new Date(),
    category: '',
    items: [],
    guest: '',
    reference: '',
    remarks: ''
  });

  // Reference form state
  const [referenceFormData, setReferenceFormData] = useState({
    name: '',
    phoneNumber: '',
    secondaryPhoneNumber: '',
    classification: 'A',
    type: 'Interior',
    station: null // Changed to null for react-select
  });

  // Guest form state
  const [guestFormData, setGuestFormData] = useState({
    guestName: '',
    phoneNo: '',
    secondaryPhoneNo: '',
    station: null, // Changed to null for react-select
    reference: '',
    salesperson: null // Changed to null for react-select
  });

  // Select options state
  const [stationOptions, setStationOptions] = useState([]);
  const [salespersonOptions, setSalespersonOptions] = useState([]);

  // Reference selection modal state
  const [showReferenceSelectionModal, setShowReferenceSelectionModal] = useState(false);
  const [referenceSearchTerm, setReferenceSearchTerm] = useState('');
  const [referencePage, setReferencePage] = useState(1);
  const [referenceTotalPages, setReferenceTotalPages] = useState(1);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [referencesForSelection, setReferencesForSelection] = useState([]);
  const [selectedReference, setSelectedReference] = useState(null);

  // Guest selection modal state
  const [showGuestSelectionModal, setShowGuestSelectionModal] = useState(false);
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  const [guestPage, setGuestPage] = useState(1);
  const [guestTotalPages, setGuestTotalPages] = useState(1);
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestsForSelection, setGuestsForSelection] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchActivityLogs();
    fetchFilterOptions();
    fetchReferences();
    fetchGuests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterDate, filterCategory, filterItem, filterStatus]);

  // Add a separate useEffect for initialization that runs only once
  useEffect(() => {
    fetchStations();
    fetchSalespersons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        date: filterDate,
        category: filterCategory,
        item: filterItem,
        status: filterStatus,
        userId: user.user._id // Filter by current user's logs only
      };

      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await axios.get(`${API_BASE}/activity-logs-new`, { params });
      
      if (response.data.success) {
        setActivityLogs(response.data.data);
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalCount(response.data.pagination.totalCount || 0);
        prepareChartData(response.data.data);
        preparePieChartData(response.data.data);
        calculateStatistics(response.data.data);
      } else {
        toast.error('Failed to fetch activity logs');
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/activity-logs-new/filters/options`);
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchReferences = async () => {
    try {
      const response = await axios.get(`${API_BASE}/references`, {
        params: { userCompanyId: user.user.company._id || user.user.company }
      });
      if (response.data.success) {
        setReferences(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching references:', error);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await axios.get(`${API_BASE}/activity-logs-new/guests/list`, {
        params: { userCompanyId: user.user.company._id || user.user.company }
      });
      if (response.data.success) {
        setGuests(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/activity-logs-new/stations/list`);
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        // Format stations for react-select
        const options = response.data.data.map(station => ({
          value: station._id,
          label: station.name
        }));
        setStationOptions(options);
        setStations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
      toast.error('Failed to load stations');
    }
  };

  const fetchSalespersons = async () => {
    try {
      const companyId = user.user.company._id || user.user.company;
      console.log('Fetching salespersons for company:', companyId);
      
      const response = await axios.get(`${API_BASE}/employees/company2/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Salespersons response:', response.data);
      
      if (response.data.employees) {
        // Format salespersons for react-select
        const options = response.data.employees.map(person => ({
          value: person._id,
          label: `${person.name} (${person.id || ''})`
        }));
        setSalespersonOptions(options);
        setSalespersons(response.data.employees);
        console.log('Salesperson options set:', options);
      }
    } catch (error) {
      console.error('Error fetching salespersons:', error);
      toast.error('Failed to load salespersons');
    }
  };

  // Fetch references for selection modal with pagination
  const fetchReferencesForSelection = async (page = 1, search = '') => {
    setReferenceLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        search,
        userCompanyId: user.user.company._id || user.user.company
      };

      const response = await axios.get(`${API_BASE}/references`, { params });
      
      if (response.data) {
        setReferencesForSelection(response.data.references || []);
        setReferenceTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching references for selection:', error);
      toast.error('Failed to load references');
    } finally {
      setReferenceLoading(false);
    }
  };

  // Fetch guests for selection modal with pagination
  const fetchGuestsForSelection = async (page = 1, search = '') => {
    setGuestLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        search,
        userCompanyId: user.user.company._id || user.user.company
      };

      const response = await axios.get(`${API_BASE}/guests`, { params });
      
      if (response.data && response.data.success) {
        setGuestsForSelection(response.data.data || []);
        setGuestTotalPages(response.data.pagination.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching guests for selection:', error);
      toast.error('Failed to load guests');
    } finally {
      setGuestLoading(false);
    }
  };

  const prepareChartData = (logs) => {
    // Get last 7 days
    const last7Days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last7Days.push({
        date: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }

    // Count logs per day
    const logCounts = last7Days.map(day => {
      const count = logs.filter(log => {
        const logDate = new Date(log.date).toISOString().split('T')[0];
        return logDate === day.date;
      }).length;
      return count;
    });

    setChartData({
      labels: last7Days.map(day => day.label),
      datasets: [
        {
          label: 'Activity Logs',
          data: logCounts,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.1,
          fill: true
        }
      ]
    });
  };

  const preparePieChartData = (logs) => {
    const statusCounts = {
      Pending: logs.filter(log => log.status === 'Pending').length,
      Completed: logs.filter(log => log.status === 'Completed').length,
      Cancelled: logs.filter(log => log.status === 'Cancelled').length,
      Draft: logs.filter(log => log.status === 'Draft').length,
      Service: logs.filter(log => log.status === 'Service').length,
      'Quotation issued': logs.filter(log => log.status === 'Quotation issued').length
    };

    setPieChartData({
      labels: ['Pending', 'Completed', 'Cancelled', 'Draft', 'Service', 'Quotation issued'],
      datasets: [
        {
          data: [statusCounts.Pending, statusCounts.Completed, statusCounts.Cancelled, statusCounts.Draft, statusCounts.Service, statusCounts['Quotation issued']],
          backgroundColor: ['#fbbf24', '#10b981', '#ef4444', '#6b7280', '#8b5cf6', '#3b82f6'],
          borderColor: ['#f59e0b', '#059669', '#dc2626', '#4b5563', '#7c3aed', '#2563eb'],
          borderWidth: 2,
        }
      ]
    });
  };

  const calculateStatistics = (logs) => {
    const stats = {
      total: logs.length,
      pending: 0,
      completed: 0,
      cancelled: 0,
      draft: 0,
      service: 0,
      quotationIssued: 0
    };
    logs.forEach(log => {
      switch (log.status) {
        case 'Pending':
          stats.pending++;
          break;
        case 'Completed':
          stats.completed++;
          break;
        case 'Cancelled':
          stats.cancelled++;
          break;
        case 'Draft':
          stats.draft++;
          break;
        case 'Service':
          stats.service++;
          break;
        case 'Quotation issued':
          stats.quotationIssued++;
          break;
        default:
          break;
      }
    });
    setStatistics(stats);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchActivityLogs();
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        date: formData.date.toISOString().split('T')[0], // Format date for API
        createdBy: user.user._id
      };

      const response = await axios.post(`${API_BASE}/activity-logs-new`, payload);
      
      if (response.data.success) {
        toast.success('Activity log created successfully');
        setShowCreateForm(false);
        setFormData({
          date: new Date(),
          category: '',
          items: [],
          guest: '',
          reference: '',
          remarks: ''
        });
        fetchActivityLogs();
      } else {
        toast.error(response.data.message || 'Failed to create activity log');
      }
    } catch (error) {
      console.error('Error creating activity log:', error);
      toast.error(error.response?.data?.message || 'Failed to create activity log');
    }
  };

  const handleCreateReference = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!referenceFormData.name || !referenceFormData.phoneNumber || !referenceFormData.station) {
      toast.error('All fields are required');
      return;
    }

    try {
      const payload = {
        name: referenceFormData.name,
        phoneNumber: referenceFormData.phoneNumber,
        secondaryPhoneNumber: referenceFormData.secondaryPhoneNumber,
        classification: referenceFormData.classification,
        type: referenceFormData.type,
        station: referenceFormData.station.value, // Extract value from react-select object
        createdBy: user.user._id
      };

      const response = await axios.post(`${API_BASE}/references`, payload);
      
      if (response.data) {
        toast.success('Reference created successfully');
        
        // Add new reference to list and reset form
        setReferences([response.data, ...references]);
        setReferenceFormData({
          name: '',
          phoneNumber: '',
          secondaryPhoneNumber: '',
          classification: 'A',
          type: 'Interior',
          station: null
        });
        
        // Close modal
        setShowReferenceModal(false);
        
        // Refresh references list
        fetchReferences();
      }
    } catch (error) {
      console.error('Error creating reference:', error);
      toast.error(error.response?.data?.error || 'Failed to create reference');
    }
  };

  const handleCreateGuest = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!guestFormData.guestName || !guestFormData.phoneNo || !guestFormData.station || 
        !guestFormData.reference || !guestFormData.salesperson) {
      toast.error('All fields are required');
      return;
    }

    try {
      const payload = {
        guestName: guestFormData.guestName,
        phoneNo: guestFormData.phoneNo,
        secondaryPhoneNo: guestFormData.secondaryPhoneNo,
        station: guestFormData.station.value, // Extract value from react-select object
        reference: guestFormData.reference,
        salesperson: guestFormData.salesperson.value, // Extract value from react-select object
        createdBy: user.user._id
      };

      const response = await axios.post(`${API_BASE}/guests`, payload);
      
      if (response.data.success) {
        toast.success('Guest created successfully');
        
        // Add new guest to list and reset form
        setGuests([response.data.data, ...guests]);
        setGuestFormData({
          guestName: '',
          phoneNo: '',
          secondaryPhoneNo: '',
          station: null,
          reference: '',
          salesperson: null
        });
        
        // Close modal
        setShowGuestModal(false);
        
        // Refresh guests list
        fetchGuests();
      } else {
        // Handle validation errors
        if (response.data.errors && Array.isArray(response.data.errors)) {
          toast.error('Validation Error: ' + response.data.errors.join(', '));
        } else {
          toast.error(response.data.message || 'Failed to create guest');
        }
      }
    } catch (error) {
      console.error('Error creating guest:', error);
      
      // Handle validation errors from axios error response
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        toast.error('Validation Error: ' + error.response.data.errors.join(', '));
      } else {
        toast.error(error.response?.data?.message || 'Failed to create guest');
      }
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        search: searchTerm,
        date: filterDate,
        category: filterCategory,
        item: filterItem,
        status: filterStatus
      };

      const response = await axios.get(`${API_BASE}/activity-logs-new/export/csv`, { 
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-logs-new-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Activity logs exported successfully');
    } catch (error) {
      console.error('Error exporting activity logs:', error);
      toast.error('Failed to export activity logs');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return {
      date: `${day}/${month}/${year}`,
      time: date.toLocaleTimeString()
    };
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800',
      'Draft': 'bg-gray-100 text-gray-800',
      'Service': 'bg-purple-100 text-purple-800',
      'Quotation issued': 'bg-blue-100 text-blue-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryColor = (category) => {
    const categoryColors = {
      'Reference': 'bg-blue-100 text-blue-800',
      'Guest': 'bg-green-100 text-green-800'
    };
    return categoryColors[category] || 'bg-gray-100 text-gray-800';
  };

  // Handle reference selection
  const handleReferenceSelect = (reference) => {
    setSelectedReference(reference);
    setFormData({ ...formData, reference: reference._id });
    setShowReferenceSelectionModal(false);
  };

  // Handle guest selection
  const handleGuestSelect = (guest) => {
    setSelectedGuest(guest);
    setFormData({ ...formData, guest: guest._id });
    setShowGuestSelectionModal(false);
  };

  // Handle reference search
  const handleReferenceSearch = (e) => {
    setReferenceSearchTerm(e.target.value);
    setReferencePage(1);
    fetchReferencesForSelection(1, e.target.value);
  };

  // Handle guest search
  const handleGuestSearch = (e) => {
    setGuestSearchTerm(e.target.value);
    setGuestPage(1);
    fetchGuestsForSelection(1, e.target.value);
  };

  // Handle reference page change
  const handleReferencePageChange = (newPage) => {
    setReferencePage(newPage);
    fetchReferencesForSelection(newPage, referenceSearchTerm);
  };

  // Handle guest page change
  const handleGuestPageChange = (newPage) => {
    setGuestPage(newPage);
    fetchGuestsForSelection(newPage, guestSearchTerm);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <FaClock className="mr-3 text-blue-600" />
          Activity Logs (New)
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors"
          >
            <FaPlus className="mr-2" />
            Create Log
          </button>
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-green-700 transition-colors"
          >
            <FaDownload className="mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Create Activity Log</h3>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <div className="relative">
                  <DatePicker
                    selected={formData.date}
                    onChange={(date) => setFormData({...formData, date: date})}
                    className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
                    dateFormat="yyyy-MM-dd"
                    required
                  />
                  <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <Select
                  value={formData.category ? { value: formData.category, label: formData.category } : null}
                  onChange={(selectedOption) => {
                    const newCategory = selectedOption ? selectedOption.value : '';
                    setFormData({
                      ...formData, 
                      category: newCategory,
                      guest: '',
                      reference: ''
                    });
                  }}
                  options={[
                    { value: 'Reference', label: 'Reference' },
                    { value: 'Guest', label: 'Guest' }
                  ]}
                  placeholder="Select Category..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: '40px',
                      height: '40px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      '&:hover': { border: '1px solid #d1d5db' },
                      '&:focus': { border: '2px solid #3b82f6', outline: 'none' }
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: '38px',
                      padding: '0 8px'
                    })
                  }}
                />
              </div>

              {/* Show Reference selection for Reference category */}
              {formData.category === 'Reference' && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">Reference *</label>
                    <button
                      type="button"
                      onClick={() => setShowReferenceModal(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <FaPlus className="mr-1" />
                      Create New
                    </button>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setShowReferenceSelectionModal(true);
                        setReferencePage(1);
                        setReferenceSearchTerm('');
                        fetchReferencesForSelection(1, '');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {selectedReference ? (
                        <span className="text-gray-900">
                          {selectedReference.name} - {selectedReference.phoneNumber} ({selectedReference.type})
                        </span>
                      ) : (
                        <span className="text-gray-500">Select Reference...</span>
                      )}
                    </button>
                    {selectedReference && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedReference(null);
                          setFormData({ ...formData, reference: '' });
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Show Guest selection for Guest category */}
              {formData.category === 'Guest' && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">Guest *</label>
                    <button
                      type="button"
                      onClick={() => setShowGuestModal(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <FaPlus className="mr-1" />
                      Create New
                    </button>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setShowGuestSelectionModal(true);
                        setGuestPage(1);
                        setGuestSearchTerm('');
                        fetchGuestsForSelection(1, '');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {selectedGuest ? (
                        <span className="text-gray-900">
                          {selectedGuest.guestName} - {selectedGuest.phoneNo}
                        </span>
                      ) : (
                        <span className="text-gray-500">Select Guest...</span>
                      )}
                    </button>
                    {selectedGuest && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedGuest(null);
                          setFormData({ ...formData, guest: '' });
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Items *</label>
                <Select
                  isMulti
                  value={['Tiles', 'Sanitary Ware', 'Kithen', 'Doors', 'Windows', 'Others']
                    .filter(item => formData.items.includes(item))
                    .map(item => ({ value: item, label: item }))}
                  onChange={(selectedOptions) => {
                    const selectedItems = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    setFormData({...formData, items: selectedItems});
                  }}
                  options={['Tiles', 'Sanitary Ware', 'Kithen', 'Doors', 'Windows', 'Others'].map(item => ({ value: item, label: item }))}
                  placeholder="Select Items..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: '40px',
                      height: 'auto',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      '&:hover': {
                        border: '1px solid #d1d5db'
                      },
                      '&:focus': {
                        border: '2px solid #3b82f6',
                        outline: 'none'
                      }
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: '4px 8px'
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#eff6ff',
                      border: '1px solid #3b82f6'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#1e40af'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#1e40af',
                      '&:hover': {
                        backgroundColor: '#3b82f6',
                        color: 'white'
                      }
                    })
                  }}
                  required={formData.items.length === 0}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Enter remarks (optional)"
                  style={{ minHeight: "80px" }}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced Filters Section */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <FaFilter className="mr-2 text-blue-600" />
              Search & Filter Activity Logs
            </h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {totalCount} total records
              </span>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterDate('');
                  setFilterCategory('');
                  setFilterItem('');
                  setFilterStatus('');
                  setCurrentPage(1);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
              >
                <FaTimes className="mr-1" />
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Search Section */}
            <div className="xl:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Activity Logs
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by guest, reference, remarks, items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 h-12"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaClock className="inline mr-1" />
                Filter by Date
              </label>
              <div className="relative">
                <DatePicker
                  selected={filterDate ? new Date(filterDate) : null}
                  onChange={(date) => setFilterDate(date ? date.toISOString().split('T')[0] : '')}
                  className="w-full px-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 h-12"
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date..."
                  isClearable
                />
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                {filterDate && (
                  <button
                    onClick={() => setFilterDate('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
            
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaTag className="inline mr-1" />
                Category
              </label>
              <Select
                value={filterCategory ? { value: filterCategory, label: filterCategory } : null}
                onChange={(selectedOption) => setFilterCategory(selectedOption ? selectedOption.value : '')}
                options={filterOptions.categories.map(category => ({ value: category, label: category }))}
                placeholder="All Categories"
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '48px',
                    height: '48px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    '&:hover': { border: '1px solid #9ca3af' },
                    '&:focus-within': { 
                      border: '2px solid #3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                    }
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: '46px',
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center'
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: '#9ca3af',
                    fontSize: '14px'
                  })
                }}
              />
            </div>
            
            {/* Item Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaBox className="inline mr-1" />
                Item
              </label>
              <Select
                value={filterItem ? { value: filterItem, label: filterItem } : null}
                onChange={(selectedOption) => setFilterItem(selectedOption ? selectedOption.value : '')}
                options={filterOptions.items.map(item => ({ value: item, label: item }))}
                placeholder="All Items"
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '48px',
                    height: '48px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    '&:hover': { border: '1px solid #9ca3af' },
                    '&:focus-within': { 
                      border: '2px solid #3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                    }
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: '46px',
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center'
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: '#9ca3af',
                    fontSize: '14px'
                  })
                }}
              />
            </div>
            
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaCheckCircle className="inline mr-1" />
                Status
              </label>
              <Select
                value={filterStatus ? { value: filterStatus, label: filterStatus } : null}
                onChange={(selectedOption) => setFilterStatus(selectedOption ? selectedOption.value : '')}
                options={filterOptions.statuses.map(status => ({ value: status, label: status }))}
                placeholder="All Status"
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '48px',
                    height: '48px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    '&:hover': { border: '1px solid #9ca3af' },
                    '&:focus-within': { 
                      border: '2px solid #3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                    }
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: '46px',
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center'
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: '#9ca3af',
                    fontSize: '14px'
                  })
                }}
              />
            </div>
            
            {/* Apply Filters Button */}
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg flex items-center justify-center hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <FaSearch className="mr-2" />
                Apply Filters
              </button>
            </div>
          </div>
          
          {/* Active Filters Display */}
          {(searchTerm || filterDate || filterCategory || filterItem || filterStatus) && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Active filters:</span>
                {searchTerm && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Search: "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm('')}
                      className="ml-2 hover:text-blue-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                {filterDate && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Date: {(() => {
                      const date = new Date(filterDate);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}
                    <button
                      onClick={() => setFilterDate('')}
                      className="ml-2 hover:text-green-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                {filterCategory && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Category: {filterCategory}
                    <button
                      onClick={() => setFilterCategory('')}
                      className="ml-2 hover:text-purple-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                {filterItem && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Item: {filterItem}
                    <button
                      onClick={() => setFilterItem('')}
                      className="ml-2 hover:text-yellow-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                {filterStatus && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Status: {filterStatus}
                    <button
                      onClick={() => setFilterStatus('')}
                      className="ml-2 hover:text-red-600"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity Logs Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <FaClock className="mr-2" />
                  Date
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <FaUser className="mr-2" />
                  Contact
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Remarks
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Loading activity logs...</span>
                  </div>
                </td>
              </tr>
            ) : activityLogs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  No activity logs found
                </td>
              </tr>
            ) : (
              activityLogs.map((log, index) => {
                const { date } = formatDateTime(log.date);
                return (
                  <tr key={log._id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {log.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {log.guest?.guestName || 
                         log.reference?.name ||
                         'No Contact'}
                      </div>
                      {log.guest?.phoneNo && (
                        <div className="text-sm text-gray-500">{log.guest.phoneNo}</div>
                      )}
                      {log.reference?.phoneNumber && (
                        <div className="text-sm text-gray-500">{log.reference.phoneNumber}</div>
                      )}
                      {log.reference?.type && (
                        <div className="text-xs text-gray-400">({log.reference.type})</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(log.category)}`}>
                        {log.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Array.isArray(log.items) ? log.items.join(', ') : log.items || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {log.remarks}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing page {currentPage} of {totalPages} ({totalCount} total records)
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNumber = Math.max(1, currentPage - 2) + i;
              if (pageNumber > totalPages) return null;
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`px-3 py-1 border rounded-md text-sm font-medium ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Statistics and Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Line Chart */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Activity Logs - Last 7 Days</h3>
          <div className="h-64">
            <Line 
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Pie Chart with Statistics */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Status Distribution</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-48">
              <Pie 
                data={pieChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        boxWidth: 12,
                        padding: 10,
                      }
                    },
                  },
                }}
              />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                <span className="text-sm font-medium text-gray-600">Total</span>
                <span className="text-lg font-bold text-blue-600">{statistics.total}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                <span className="text-sm font-medium text-yellow-700">Pending</span>
                <span className="text-lg font-bold text-yellow-600">{statistics.pending}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                <span className="text-sm font-medium text-green-700">Completed</span>
                <span className="text-lg font-bold text-green-600">{statistics.completed}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                <span className="text-sm font-medium text-red-700">Cancelled</span>
                <span className="text-lg font-bold text-red-600">{statistics.cancelled}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                <span className="text-sm font-medium text-gray-700">Draft</span>
                <span className="text-lg font-bold text-gray-600">{statistics.draft}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                <span className="text-sm font-medium text-purple-700">Service</span>
                <span className="text-lg font-bold text-purple-600">{statistics.service}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                <span className="text-sm font-medium text-blue-700">Quotation Issued</span>
                <span className="text-lg font-bold text-blue-600">{statistics.quotationIssued}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reference Creation Modal */}
      {showReferenceModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">Add New Reference</h3>
                <button
                  onClick={() => setShowReferenceModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>
            </div>
            <form onSubmit={handleCreateReference} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={referenceFormData.name}
                    onChange={(e) => setReferenceFormData({ ...referenceFormData, name: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder="Enter reference name"
                    style={{ textTransform: 'uppercase' }}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    id="phoneNumber"
                    value={referenceFormData.phoneNumber}
                    onChange={(e) => setReferenceFormData({ ...referenceFormData, phoneNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="secondaryPhoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Phone Number (Optional)
                  </label>
                  <input
                    type="text"
                    id="secondaryPhoneNumber"
                    value={referenceFormData.secondaryPhoneNumber}
                    onChange={(e) => setReferenceFormData({ ...referenceFormData, secondaryPhoneNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder="Enter secondary phone number (optional)"
                  />
                </div>
                <div>
                  <label htmlFor="classification" className="block text-sm font-medium text-gray-700 mb-1">
                    Classification *
                  </label>
                  <Select
                    id="classification"
                    value={{ value: referenceFormData.classification, label: referenceFormData.classification }}
                    onChange={(option) => setReferenceFormData({ ...referenceFormData, classification: option.value })}
                    options={[
                      { value: 'A', label: 'A' },
                      { value: 'B', label: 'B' },
                      { value: 'C', label: 'C' },
                      { value: 'D', label: 'D' },
                      { value: 'E', label: 'E' }
                    ]}
                    className="w-full"
                    classNamePrefix="select"
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '42px',
                        border: state.isFocused ? '2px solid #3b82f6' : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                        '&:hover': { border: state.isFocused ? '2px solid #3b82f6' : '1px solid #9ca3af' },
                        outline: 'none'
                      })
                    }}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <Select
                    id="type"
                    value={{ value: referenceFormData.type, label: referenceFormData.type }}
                    onChange={(option) => setReferenceFormData({ ...referenceFormData, type: option.value })}
                    options={[
                      { value: 'Interior', label: 'Interior' },
                      { value: 'Architect', label: 'Architect' },
                      { value: 'Builder', label: 'Builder' },
                    ]}
                    className="w-full"
                    classNamePrefix="select"
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '42px',
                        border: state.isFocused ? '2px solid #3b82f6' : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                        '&:hover': { border: state.isFocused ? '2px solid #3b82f6' : '1px solid #9ca3af' },
                        outline: 'none'
                      })
                    }}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="refStation" className="block text-sm font-medium text-gray-700 mb-1">
                    Station *
                  </label>
                  <Select
                    id="refStation"
                    value={referenceFormData.station}
                    onChange={(option) => setReferenceFormData({ ...referenceFormData, station: option })}
                    options={stationOptions}
                    className="w-full"
                    classNamePrefix="select"
                    placeholder="Select station..."
                    isClearable
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '42px',
                        border: state.isFocused ? '2px solid #3b82f6' : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                        '&:hover': { border: state.isFocused ? '2px solid #3b82f6' : '1px solid #9ca3af' },
                        outline: 'none'
                      })
                    }}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowReferenceModal(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 px-4 border border-transparent rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Guest Creation Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">Add New Guest</h3>
                <button
                  onClick={() => setShowGuestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>
            </div>
            <form onSubmit={handleCreateGuest} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="guestName" className="block text-sm font-medium text-gray-700 mb-1">
                    Guest Name *
                  </label>
                  <input
                    type="text"
                    id="guestName"
                    value={guestFormData.guestName}
                    onChange={(e) => setGuestFormData({ ...guestFormData, guestName: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder="Enter guest name"
                    style={{ textTransform: 'uppercase' }}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phoneNo" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    id="phoneNo"
                    value={guestFormData.phoneNo}
                    onChange={(e) => setGuestFormData({ ...guestFormData, phoneNo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="secondaryPhoneNo" className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Phone Number (Optional)
                  </label>
                  <input
                    type="text"
                    id="secondaryPhoneNo"
                    value={guestFormData.secondaryPhoneNo}
                    onChange={(e) => setGuestFormData({ ...guestFormData, secondaryPhoneNo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder="Enter secondary phone number (optional)"
                  />
                </div>
                <div>
                  <label htmlFor="station" className="block text-sm font-medium text-gray-700 mb-1">
                    Station *
                  </label>
                  <Select
                    id="station"
                    value={guestFormData.station}
                    onChange={(option) => setGuestFormData({ ...guestFormData, station: option })}
                    options={stationOptions}
                    className="w-full"
                    classNamePrefix="select"
                    placeholder="Select station..."
                    isClearable
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '42px',
                        border: state.isFocused ? '2px solid #3b82f6' : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                        '&:hover': { border: state.isFocused ? '2px solid #3b82f6' : '1px solid #9ca3af' },
                        outline: 'none'
                      })
                    }}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1">
                    Reference *
                  </label>
                  <input
                    type="text"
                    id="reference"
                    value={guestFormData.reference}
                    onChange={(e) => setGuestFormData({ ...guestFormData, reference: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder="Enter reference"
                    style={{ textTransform: 'uppercase' }}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="salesperson" className="block text-sm font-medium text-gray-700 mb-1">
                    Salesperson *
                  </label>
                  <Select
                    id="salesperson"
                    value={guestFormData.salesperson}
                    onChange={(option) => setGuestFormData({ ...guestFormData, salesperson: option })}
                    options={salespersonOptions}
                    className="w-full"
                    classNamePrefix="select"
                    placeholder="Select salesperson..."
                    isClearable
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '42px',
                        border: state.isFocused ? '2px solid #3b82f6' : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                        '&:hover': { border: state.isFocused ? '2px solid #3b82f6' : '1px solid #9ca3af' },
                        outline: 'none'
                      })
                    }}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowGuestModal(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 px-4 border border-transparent rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reference Selection Modal */}
      {showReferenceSelectionModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Select Reference</h3>
              <button
                onClick={() => setShowReferenceSelectionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search references by name or phone..."
                    value={referenceSearchTerm}
                    onChange={handleReferenceSearch}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Reference List */}
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                {referenceLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : referencesForSelection.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    No references found
                  </div>
                ) : (
                  referencesForSelection.map(reference => (
                    <div
                      key={reference._id}
                      onClick={() => handleReferenceSelect(reference)}
                      className="flex items-center justify-between p-4 border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {reference.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {reference.phoneNumber} • {reference.type} • {reference.classification}
                        </div>
                        {reference.station && (
                          <div className="text-xs text-gray-400">
                            Station: {reference.station.name}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        {(() => {
                          const date = new Date(reference.createdAt);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        })()}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {referenceTotalPages > 1 && (
                <div className="flex items-center justify-center space-x-2 mt-4">
                  <button
                    onClick={() => handleReferencePageChange(Math.max(referencePage - 1, 1))}
                    disabled={referencePage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: referenceTotalPages }, (_, i) => i + 1).map(pageNumber => {
                    if (pageNumber === 1 || pageNumber === referenceTotalPages || 
                        (pageNumber >= referencePage - 1 && pageNumber <= referencePage + 1)) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handleReferencePageChange(pageNumber)}
                          className={`px-3 py-1 border rounded-md text-sm font-medium ${
                            referencePage === pageNumber
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => handleReferencePageChange(Math.min(referencePage + 1, referenceTotalPages))}
                    disabled={referencePage === referenceTotalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Guest Selection Modal */}
      {showGuestSelectionModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Select Guest</h3>
              <button
                onClick={() => setShowGuestSelectionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search guests by name or phone..."
                    value={guestSearchTerm}
                    onChange={handleGuestSearch}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Guest List */}
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                {guestLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : guestsForSelection.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    No guests found
                  </div>
                ) : (
                  guestsForSelection.map(guest => (
                    <div
                      key={guest._id}
                      onClick={() => handleGuestSelect(guest)}
                      className="flex items-center justify-between p-4 border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {guest.guestName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {guest.phoneNo} • Reference: {guest.reference}
                        </div>
                        {guest.station && (
                          <div className="text-xs text-gray-400">
                            Station: {guest.station.name}
                          </div>
                        )}
                        {guest.salesperson && (
                          <div className="text-xs text-gray-400">
                            Salesperson: {guest.salesperson.name}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        {(() => {
                          const date = new Date(guest.createdAt);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        })()}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {guestTotalPages > 1 && (
                <div className="flex items-center justify-center space-x-2 mt-4">
                  <button
                    onClick={() => handleGuestPageChange(Math.max(guestPage - 1, 1))}
                    disabled={guestPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: guestTotalPages }, (_, i) => i + 1).map(pageNumber => {
                    if (pageNumber === 1 || pageNumber === guestTotalPages || 
                        (pageNumber >= guestPage - 1 && pageNumber <= guestPage + 1)) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handleGuestPageChange(pageNumber)}
                          className={`px-3 py-1 border rounded-md text-sm font-medium ${
                            guestPage === pageNumber
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => handleGuestPageChange(Math.min(guestPage + 1, guestTotalPages))}
                    disabled={guestPage === guestTotalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogsNew;
